/**
 * What a door phone keeps for check-in with no signal (Event Tickets plan
 * 2026-10-19, U8, KTD13, R23):
 *   - the guest list, for scanning and searching offline;
 *   - the outbox, admits made offline that still have to be sent.
 *
 * IndexedDB, with localStorage when IndexedDB cannot open (private windows,
 * old browsers). Keyed by the signed-in user and the event, so a second
 * person on the same phone never sees the first person's list.
 *
 * Guest lists are cleared on sign-out (clearDoorStorage, called from
 * AuthContext); lists and unsent check-ins are cleared 24 hours after the
 * event ends (sweepDoorStorage, run when the page opens). The list holds a
 * hash of each ticket code, never the code (doorCheckin.ts).
 */

import {
  safeLocalStorageGetItem,
  safeLocalStorageKey,
  safeLocalStorageLength,
  safeLocalStorageRemoveItem,
  safeLocalStorageSetItem,
} from '@/utils/storage/safeLocalStorage';
import { doorListExpired, type DoorGuestList, type DoorOutboxEntry } from '@/utils/eventTickets/doorCheckin';

const DB_NAME = 'cheervue-door-checkin';
const STORE = 'kv';
const LOCAL_PREFIX = 'cheervue_door_checkin:';

export interface SavedDoorList {
  list: DoorGuestList;
  /** When this phone last got the list from the server (ms). */
  savedAt: number;
}

interface SavedOutbox {
  eventEnd: string;
  entries: DoorOutboxEntry[];
}

const listKey = (userId: string, eventId: string) => `list:${userId}:${eventId}`;
const outboxKey = (userId: string, eventId: string) => `outbox:${userId}:${eventId}`;

// ── a tiny key-value store ──────────────────────────────────────────────────

let dbPromise: Promise<IDBDatabase | null> | null = null;

function openDb(): Promise<IDBDatabase | null> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve) => {
    try {
      if (typeof indexedDB === 'undefined') return resolve(null);
      const req = indexedDB.open(DB_NAME, 1);
      req.onupgradeneeded = () => req.result.createObjectStore(STORE);
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => resolve(null);
      req.onblocked = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
  return dbPromise;
}

function idbRequest<T>(db: IDBDatabase, mode: IDBTransactionMode, run: (s: IDBObjectStore) => IDBRequest): Promise<T | undefined> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, mode);
    const req = run(tx.objectStore(STORE));
    tx.oncomplete = () => resolve(req.result as T);
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  });
}

async function getItem<T>(key: string): Promise<T | null> {
  const db = await openDb();
  if (db) {
    try {
      return ((await idbRequest<T>(db, 'readonly', (s) => s.get(key))) ?? null) as T | null;
    } catch {
      // Fall through to the localStorage copy.
    }
  }
  const raw = safeLocalStorageGetItem(LOCAL_PREFIX + key);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

async function setItem(key: string, value: unknown): Promise<void> {
  const db = await openDb();
  if (db) {
    try {
      await idbRequest(db, 'readwrite', (s) => s.put(value, key));
      return;
    } catch {
      // Fall through to localStorage.
    }
  }
  safeLocalStorageSetItem(LOCAL_PREFIX + key, JSON.stringify(value));
}

async function removeItem(key: string): Promise<void> {
  const db = await openDb();
  if (db) {
    try {
      await idbRequest(db, 'readwrite', (s) => s.delete(key));
    } catch {
      // The localStorage copy is removed below either way.
    }
  }
  safeLocalStorageRemoveItem(LOCAL_PREFIX + key);
}

async function allKeys(): Promise<string[]> {
  const keys = new Set<string>();
  const db = await openDb();
  if (db) {
    try {
      for (const k of (await idbRequest<IDBValidKey[]>(db, 'readonly', (s) => s.getAllKeys())) ?? []) keys.add(String(k));
    } catch {
      // Only the localStorage keys then.
    }
  }
  for (let i = 0; i < safeLocalStorageLength(); i += 1) {
    const k = safeLocalStorageKey(i);
    if (k?.startsWith(LOCAL_PREFIX)) keys.add(k.slice(LOCAL_PREFIX.length));
  }
  return Array.from(keys);
}

// ── the door's own data ─────────────────────────────────────────────────────

export function readDoorList(userId: string, eventId: string): Promise<SavedDoorList | null> {
  return getItem<SavedDoorList>(listKey(userId, eventId));
}

export function saveDoorList(userId: string, eventId: string, saved: SavedDoorList): Promise<void> {
  return setItem(listKey(userId, eventId), saved);
}

/** A staff member taken off the list keeps no copy of it (R21). */
export function forgetDoorList(userId: string, eventId: string): Promise<void> {
  return removeItem(listKey(userId, eventId));
}

export async function readDoorOutbox(userId: string, eventId: string): Promise<DoorOutboxEntry[]> {
  return (await getItem<SavedOutbox>(outboxKey(userId, eventId)))?.entries ?? [];
}

export async function saveDoorOutbox(userId: string, eventId: string, eventEnd: string, entries: DoorOutboxEntry[]): Promise<void> {
  if (entries.length === 0) return removeItem(outboxKey(userId, eventId));
  return setItem(outboxKey(userId, eventId), { eventEnd, entries } satisfies SavedOutbox);
}

/**
 * Run when the check-in page opens: drops every saved list that belongs to
 * someone else, and everything kept for an event that ended over 24 hours ago.
 */
export async function sweepDoorStorage(currentUserId: string, nowMs: number): Promise<void> {
  for (const key of await allKeys()) {
    const [kind, userId] = key.split(':');
    if (kind === 'list') {
      const saved = await getItem<SavedDoorList>(key);
      if (userId !== currentUserId || !saved?.list?.event || doorListExpired(saved.list.event.end_time, nowMs)) await removeItem(key);
    } else if (kind === 'outbox') {
      const saved = await getItem<SavedOutbox>(key);
      if (!saved || doorListExpired(saved.eventEnd, nowMs)) await removeItem(key);
    } else {
      await removeItem(key);
    }
  }
}

/**
 * Sign-out: every saved guest list goes. Check-ins still waiting to send stay:
 * people were let in, so the same staff member's next sign-in sends them, and
 * the sweep drops them 24 hours after the event. They hold order ids and, for
 * an "Admit anyway", the code that was scanned; never names.
 */
export async function clearDoorStorage(): Promise<void> {
  for (const key of await allKeys()) {
    if (!key.startsWith('outbox:')) await removeItem(key);
  }
}

/** Test hook: forget the opened database so a test can swap storage. */
export function resetDoorStoreForTests(): void {
  dbPromise = null;
}
